import React, { useEffect, useState } from "react";
import { Box, Button, Checkbox, ColorPicker, createListCollection, Field, Heading, HStack, Input, parseColor, Select, Separator, Text } from "@chakra-ui/react";
import { rgbaToHex, saveToCache, showToast } from "../../../utilies/Utility";
import { togglePolygonColors } from "../../../utilies/OsmUtility";
import { toggleHeightFilteredPoints } from "../../../utilies/OtherUtility";
import { useAppStore } from "../../../context/store";
import { useMapObjects } from "../../../context/MapObjectsContext";
import Type from "../../../utilies/Types";

function SettingsMenuGeneral() {
  // ===== state =====
  const { mapObjects } = useMapObjects();
  const snappingOptions = useAppStore((s) => s.snappingOptions);
  const configSpeedProfile = useAppStore((s) => s.configSpeedProfile);
  const directionArrowSetting = useAppStore((s) => s.directionArrowSetting);
  const db = useAppStore((s) => s.db);
  const defaultCity = useAppStore((s) => s.defaultCity);
  const city = useAppStore((s) => s.city);

  const filterNodeMinHeight = useAppStore((s) => s.filterNodeMinHeight);
  const filterNodeMaxHeight = useAppStore((s) => s.filterNodeMaxHeight);
  const simplifyToleranceSetting = useAppStore((s) => s.simplifyToleranceSetting);
  const lineSampleRate = useAppStore((s) => s.lineSampleRate);
  const autoSketcherDistance = useAppStore((s) => s.autoSketcherDistance);

  // ===== setters / refs =====
  const viewerRef = useAppStore.getState().viewerRef;
  const setSnappingOptions = useAppStore.getState().setSnappingOptions;

  const setDirectionArrowSetting = useAppStore.getState().setDirectionArrowSetting;
  const setDb = useAppStore.getState().setDb;
  const setDefaultCity = useAppStore.getState().setDefaultCity;

  const setSimplifyToleranceSetting = useAppStore.getState().setSimplifyToleranceSetting;
  const setLineSampleRate = useAppStore.getState().setLineSampleRate;
  const setAutoSketcherDistance = useAppStore.getState().setAutoSketcherDistance;

  const [isSpeedLimitToggled, setIsSpeedLimitToggled] = useState(false);
  const [isNodeHeightToggled, setIsNodeHeightToggled] = useState(false);

  const settings = useAppStore((s) => s.settings);
  const setSettings = useAppStore((s) => s.setSettings);

  const [cityOptions, setCityOptions] = useState(
    createListCollection({
      items: [],
    }),
  );

  useEffect(() => {
    if (city) {
      let temp = [];
      for (const key in city) {
        temp.push({ label: city[key].name, value: city[key].name.toLowerCase() });
      }
      setCityOptions(
        createListCollection({
          items: temp,
          itemToString: (item) => item.label,
          itemToValue: (item) => item.value,
        }),
      );
    }
  }, [city]);

  const settingSpeedLimitToggle = () => {
    for (const key in mapObjects) {
      if (mapObjects[key].view_type == "lanelet") {
        const newToggle = !isSpeedLimitToggled;
        setIsSpeedLimitToggled(newToggle);
        togglePolygonColors(mapObjects[key].osmDataSource, configSpeedProfile, newToggle);
      }
    }
    saveToCache("setting_speed_limit", configSpeedProfile);
  };

  const settingNodeHeightToggle = () => {
    let temp = [];
    for (const key in mapObjects) {
      if (mapObjects[key].view_type == "lanelet") {
        temp.push(mapObjects[key].osmDataSource);
      } else {
        temp.push(mapObjects[key].dataSource);
      }
    }
    const newToggle = !isNodeHeightToggled;
    setIsNodeHeightToggled(newToggle);
    toggleHeightFilteredPoints(viewerRef.current, temp, filterNodeMinHeight, filterNodeMaxHeight);
  };

  const clearCache = () => {
    const keysToRemove = ["basic_geojson_style", "setting_speed_limit", "default_osm_style"];
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
    showToast("The cache was cleared", Type.NOTIFICATION.SUCCESS);
  };

  return (
    <>
      <HStack width={"full"} gap={5} mb={3}>
        <Box>
          <Heading fontSize={"md"}>Snapping Settings</Heading>
          <HStack gap={5} mt={2}>
            <Checkbox.Root
              checked={snappingOptions.vertex}
              onChange={(v) => {
                setSnappingOptions((prev) => ({ ...prev, vertex: v.target.checked }));
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>Vertex</Checkbox.Label>
            </Checkbox.Root>
            <Checkbox.Root
              checked={snappingOptions.midpoint}
              onChange={(v) => {
                setSnappingOptions((prev) => ({ ...prev, midpoint: v.target.checked }));
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>Midpoint</Checkbox.Label>
            </Checkbox.Root>
          </HStack>
        </Box>
      </HStack>

      <Separator my={3} />

      <Box mb={3}>
        <Heading fontSize={"md"}>Arrow Appearance</Heading>
        <HStack gap={5} mt={2}>
          <ColorPicker.Root
            value={parseColor(directionArrowSetting.color)}
            onValueChange={(v) => {
              const c = rgbaToHex(v.valueAsString);
              setDirectionArrowSetting((prev) => ({
                ...prev,
                color: c,
              }));
              let temp = directionArrowSetting;
              temp.color = c;
              saveToCache("setting_arrow", temp);
            }}
          >
            <ColorPicker.HiddenInput />
            <ColorPicker.Label>Color</ColorPicker.Label>
            <ColorPicker.Control>
              <ColorPicker.Input />
              <ColorPicker.Trigger />
            </ColorPicker.Control>
            <ColorPicker.Positioner>
              <ColorPicker.Content>
                <ColorPicker.Area />
                <HStack>
                  <ColorPicker.EyeDropper size="xs" variant="outline" />
                  <ColorPicker.Sliders />
                </HStack>
              </ColorPicker.Content>
            </ColorPicker.Positioner>
          </ColorPicker.Root>

          <Field.Root mb={2} width={"fit-content"}>
            <Field.Label>Scale</Field.Label>
            <Input
              placeholder="Enter Priority"
              type="number"
              value={directionArrowSetting.scale}
              onChange={(v) => {
                let w = parseFloat(v.target.value);

                if (!isNaN(w)) {
                  w = parseFloat(w.toFixed(2));
                  setDirectionArrowSetting((prev) => ({
                    ...prev,
                    scale: w,
                  }));
                  let temp = directionArrowSetting;
                  temp.scale = w;
                  saveToCache("setting_arrow", temp);
                } else {
                  w = 0.1;
                  setDirectionArrowSetting((prev) => ({
                    ...prev,
                    scale: w,
                  }));
                  let temp = directionArrowSetting;
                  temp.scale = w;
                  saveToCache("setting_arrow", temp);
                }
              }}
            />
          </Field.Root>
        </HStack>
      </Box>

      <Separator my={3} />

      <HStack gap={3} mb={3}>
        <Box>
          <Heading fontSize={"sm"}>Simplify Tolerance (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Distance"
                type="number"
                value={simplifyToleranceSetting?.distance}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      distance: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.distance = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  } else {
                    w = 0.1;
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      distance: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.distance = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>

        <Box>
          <Heading fontSize={"sm"}>Generate Point Tolerance (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Distance"
                type="number"
                value={simplifyToleranceSetting?.generate_distance}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      generate_distance: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.generate_distance = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  } else {
                    w = 1;
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      generate_distance: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.generate_distance = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>

        <Box>
          <Heading fontSize={"sm"}>Line Sample Rate (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter ..."
                type="number"
                value={lineSampleRate}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    setLineSampleRate(parseFloat(w.toFixed(2)));
                  } else {
                    setLineSampleRate(0);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>
      </HStack>

      <HStack gap={3} mb={3}>
        <Box>
          <Heading fontSize={"sm"}>Rearrange Points (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Rearrange Points ... "
                type="number"
                value={simplifyToleranceSetting?.rearrange}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      rearrange: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.rearrange = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  } else {
                    w = 0.1;
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      rearrange: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.rearrange = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>

        <Box>
          <Heading fontSize={"sm"}>Find Break Points Angle</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter angle "
                type="number"
                value={simplifyToleranceSetting?.find_break_points}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      find_break_points: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.find_break_points = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  } else {
                    w = 0.1;
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      find_break_points: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.find_break_points = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>

        <Box>
          <Heading fontSize={"sm"}>Find Close Point Distance</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter distance "
                type="number"
                value={simplifyToleranceSetting?.find_close_point}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      find_close_point: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.find_close_point = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  } else {
                    w = 0.01;
                    setSimplifyToleranceSetting((prev) => ({
                      ...prev,
                      find_close_point: w,
                    }));
                    let temp = simplifyToleranceSetting;
                    temp.find_close_point = w;
                    saveToCache("setting_simplify_tolerance", temp);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>
      </HStack>

      <HStack gap={3} mb={3}>
        <Box>
          <Heading fontSize={"sm"}>Auto Sketcher (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Auto Sketcher ... "
                type="number"
                value={autoSketcherDistance}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setAutoSketcherDistance(w);
                  } else {
                    w = 0.01;
                    setAutoSketcherDistance(w);
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>
      </HStack>

      <Separator my={3} />

      <HStack gap={3} mb={3}>
        <Box>
          <Heading fontSize={"sm"}>Lane Connector Radius (meter)</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Lane Connector Radius ... "
                type="number"
                value={settings?.general?.laneConnectorSearchRadiusMeters}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSettings((prev) => ({
                      ...prev,
                      general: {
                        ...(prev?.general || {}),
                        laneConnectorSearchRadiusMeters: w === "" ? 1 : w,
                      },
                    }));
                  } else {
                    w = 0.01;
                    setSettings((prev) => ({
                      ...prev,
                      general: {
                        ...(prev?.general || {}),
                        laneConnectorSearchRadiusMeters: w === "" ? 1 : w,
                      },
                    }));
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>
        <Box>
          <Heading fontSize={"sm"}>Lane Connector Lookup Angle</Heading>
          <HStack gap={5} mt={2}>
            <Field.Root mb={2} width={"fit-content"}>
              <Input
                placeholder="Enter Lane Connector Lookup Angle ... "
                type="number"
                value={settings?.general?.laneConnectorAngleDeg}
                onChange={(v) => {
                  let w = parseFloat(v.target.value);

                  if (!isNaN(w)) {
                    w = parseFloat(w.toFixed(2));
                    setSettings((prev) => ({
                      ...prev,
                      general: {
                        ...(prev?.general || {}),
                        laneConnectorAngleDeg: w === "" ? 7 : w,
                      },
                    }));
                  } else {
                    w = 0.01;
                    setSettings((prev) => ({
                      ...prev,
                      general: {
                        ...(prev?.general || {}),
                        laneConnectorAngleDeg: w === "" ? 7 : w,
                      },
                    }));
                  }
                }}
              />
            </Field.Root>
          </HStack>
        </Box>
      </HStack>

      <Separator my={3} />

      <Box mb={3}>
        <Heading fontSize={"md"}>Database</Heading>
        <HStack gap={5} mt={2}>
          <Field.Root mb={2} width={"fit-content"}>
            <Field.Label>Default Database</Field.Label>
            <Input
              placeholder="Enter database"
              type="text"
              value={db}
              onChange={(v) => {
                setDb(v.target.value);
                saveToCache("setting_db", v.target.value);
              }}
            />
          </Field.Root>
        </HStack>
      </Box>

      <Separator my={3} />

      <Box mb={3}>
        <Heading fontSize={"md"}>City</Heading>
        <Text>When selected, the map will automatically zoom to this city on the initial load.</Text>
        <HStack gap={5} mt={2}>
          <Select.Root
            collection={cityOptions}
            size="sm"
            width="50%"
            value={[defaultCity]}
            positioning={{ sameWidth: true }}
            onValueChange={(value) => {
              setDefaultCity(value.value[0]);
              saveToCache("setting_city", value.value[0]);
            }}
          >
            <Select.HiddenSelect />
            <Select.Label mt={2}>Default City</Select.Label>
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText placeholder={"Select City"} />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {cityOptions.items.map((item) => (
                  <Select.Item key={item.value} item={item} justifyContent="flex-start">
                    {item.label}
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </HStack>
      </Box>

      <Separator my={3} />

      <Box mb={3}>
        <Heading fontSize={"md"}>Clear Browser Cache</Heading>
        <Text>Remove stored settings and temporary data to fix display or loading issues.</Text>
        <Button onClick={clearCache} colorPalette={"red"} mt={2}>
          Clear
        </Button>
      </Box>
    </>
  );
}

export default SettingsMenuGeneral;
